import { DocumentNode, gql, useMutation } from '@apollo/client';
import { MenuItem, useDisclosure, useToast } from '@chakra-ui/react';
import * as React from 'react';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { logError } from '@/imports/logging/ClientLogger';
import { BellIcon } from '@/imports/ui/chakra/feather';

import {
  AnnouncePostDropdownItem_CreateAnnouncement,
  AnnouncePostDropdownItem_CreateAnnouncementVariables,
} from './graphql-types/AnnouncePostDropdownItem_CreateAnnouncement';
import {
  AnnouncePostDropdownItem_DeleteAnnouncement,
  AnnouncePostDropdownItem_DeleteAnnouncementVariables,
} from './graphql-types/AnnouncePostDropdownItem_DeleteAnnouncement';
import { AnnouncePostDropdownItem_PostFragment } from './graphql-types/AnnouncePostDropdownItem_PostFragment';

const POST_FRAGMENT = gql`
  fragment AnnouncePostDropdownItem_PostFragment on PostV2 {
    _id
    announcement {
      _id
    }
  }
`;

const CREATE_ANNOUNCEMENT = gql`
  mutation AnnouncePostDropdownItem_CreateAnnouncement(
    $data: CreateAnnouncementAndUpdatePostInput_V2!
  ) {
    createAnnouncementAndUpdatePostV2(data: $data) {
      data {
        _id
        postV2 {
          _id
          ...AnnouncePostDropdownItem_PostFragment
        }
      }
    }
  }
  ${POST_FRAGMENT}
`;

const DELETE_ANNOUNCEMENT = gql`
  mutation AnnouncePostDropdownItem_DeleteAnnouncement(
    $data: DeleteAnnouncementAndUpdatePostInput_V2!
  ) {
    deleteAnnouncementAndUpdatePostV2(data: $data) {
      data {
        _id
        ...AnnouncePostDropdownItem_PostFragment
      }
    }
  }
  ${POST_FRAGMENT}
`;

export const AnnouncePostDropdownItem: React.FC<{
  post: AnnouncePostDropdownItem_PostFragment;
}> & {
  postFragment: DocumentNode;
} = (props) => {
  const { post } = props;
  const confirmDialog = useDisclosure();

  const [createAnnouncement] = useMutation<
    AnnouncePostDropdownItem_CreateAnnouncement,
    AnnouncePostDropdownItem_CreateAnnouncementVariables
  >(CREATE_ANNOUNCEMENT, {
    update(cache) {
      cache.evict({
        id: `ROOT_QUERY`,
        fieldName: 'Announcements_AnnouncementFeedV2',
      });
      cache.evict({
        id: `ROOT_QUERY`,
        fieldName: 'announcementFeedV2',
      });
      cache.gc();
    },
  });
  const [deleteAnnouncement] = useMutation<
    AnnouncePostDropdownItem_DeleteAnnouncement,
    AnnouncePostDropdownItem_DeleteAnnouncementVariables
  >(DELETE_ANNOUNCEMENT, {
    update(cache) {
      cache.evict({
        id: `ROOT_QUERY`,
        fieldName: 'announcementFeedV2',
      });
      cache.gc();
    },
  });

  const toast = useToast();

  const onConfirm = async (): Promise<void> => {
    try {
      if (!!post.announcement?._id) {
        await deleteAnnouncement({
          variables: { data: { postId: post._id } },
        });
      } else {
        await createAnnouncement({
          variables: { data: { postId: post._id } },
        });
      }
      toast({
        title: `Successfully ${
          !!post.announcement?._id
            ? 'removed post from announcements'
            : 'added post to announcements'
        }.`,
        status: 'success',
      });
    } catch (err) {
      toast({
        title: `Failed to ${
          !!post.announcement?._id
            ? 'remove post from announcements'
            : 'add post to announcements'
        }.`,
        status: 'error',
      });
      logError(err);
    }
  };

  return (
    <>
      <MenuItem icon={<BellIcon />} onClick={confirmDialog.onOpen}>
        {!!post.announcement?._id
          ? 'Remove from Announcements'
          : 'Add to Announcements'}
      </MenuItem>
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={confirmDialog.onClose}
        title={
          !!post.announcement?._id
            ? 'Remove Post from Announcements'
            : 'Add Post to Announcements'
        }
        body={
          !!post.announcement?._id
            ? 'This will remove post from announcements in the group. Announcements are shown at the top of the feed.'
            : 'This will add post to announcements in the group. Announcements are shown at the top of the feed.'
        }
        confirmButtonText={'Confirm'}
        onConfirm={onConfirm}
      />
    </>
  );
};

AnnouncePostDropdownItem.postFragment = POST_FRAGMENT;
